import { SharedModule } from 'src/app/shared/shared.module';
import { CoreModule } from './core/core.module';
import { BrowserModule } from '@angular/platform-browser';
import { NgModule, APP_INITIALIZER, ErrorHandler } from '@angular/core';
import { HttpClient, HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { enableProdMode } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ToastrModule } from 'ngx-toastr';
import { MissingTranslationHandler, MissingTranslationHandlerParams } from '@ngx-translate/core';
import { LoggerModule, NgxLoggerLevel } from 'ngx-logger';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { AppComponent } from './app.component';
import { environment } from '../environments/environment';
import { AppConfigService } from './core/service/app-config.service';
import { JwtInterceptorService } from './core/service/jwt-interceptor.service';
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { GlobalErrorHandler } from './core/error-handler/error-handler';
import { EditorModule } from '@progress/kendo-angular-editor';
import { KendoMessageService } from './core/service/kendo-message.service';
import { MessageService } from '@progress/kendo-angular-l10n';
import { APP_BASE_HREF, PlatformLocation } from '@angular/common';
import { ICON_SETTINGS, IconSettingsService } from '@progress/kendo-angular-icons';
import { IconService } from './core/service/icon.service';
import { IntercomModule } from 'ng-intercom-x';
import { ChannelsChatModule } from './chat/chat.module';
import { provideNgxWebstorage, withLocalStorage, withNgxWebstorageConfig, withSessionStorage } from 'ngx-webstorage';
import { ServiceWorkerModule } from '@angular/service-worker';

if (environment.production)
{
  enableProdMode();
}

// Application initializer
const appInitializerFn = (appConfig: AppConfigService) => () => appConfig.loadAppConfig();
const getBaseHref = (platformLocation: PlatformLocation): string => platformLocation.getBaseHrefFromDOM();

// AoT requires an exported function for factories
export function createTranslateLoader(http: HttpClient): TranslateHttpLoader
{
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

// Custom handler to provide log when missing translation is found
export class CustomTranslationHandler implements MissingTranslationHandler
{
  public handle(params: MissingTranslationHandlerParams): string
  {
    console.warn('Missing Translation for key = ' + params.key);
    return params.key;
  }
}

@NgModule({
  declarations: [
    AppComponent
  ],
  bootstrap: [
    AppComponent
  ],
  imports: [
    BrowserModule,
    LoggerModule.forRoot({
      level: environment.production ? NgxLoggerLevel.ERROR : NgxLoggerLevel.TRACE
    }),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient]
      },
      missingTranslationHandler: { provide: MissingTranslationHandler, useClass: CustomTranslationHandler },
      useDefaultLang: false
    }),
    BrowserAnimationsModule,
    environment.workerEnabled ? ServiceWorkerModule.register('./sw.js', { enabled: true, registrationStrategy: 'registerWhenStable:30000' }) : [],
    ToastrModule.forRoot({
      timeOut: 5000,
      positionClass: 'toast-top-center',
      closeButton: true,
      progressBar: false,
      disableTimeOut: false,
      preventDuplicates: true,
      includeTitleDuplicates: true,
      enableHtml: true,
    }),
    CoreModule,
    SharedModule,
    NgIdleKeepaliveModule.forRoot(),
    EditorModule,
    IntercomModule.forRoot({ updateOnRouterChange: true }),
    ChannelsChatModule
  ],
  providers: [
    AppConfigService,
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerFn,
      multi: true,
      deps: [AppConfigService]
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: JwtInterceptorService,
      multi: true
    },
    {
      provide: APP_BASE_HREF,
      useFactory: getBaseHref,
      deps: [PlatformLocation],
    },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler
    },
    {
      provide: MessageService,
      useClass: KendoMessageService
    },
    {
      provide: ICON_SETTINGS,
      useValue: { type: 'font' }
    },
    {
      provide: IconSettingsService,
      useClass: IconService
    },
    provideHttpClient(withInterceptorsFromDi()),
    provideNgxWebstorage(
      withNgxWebstorageConfig({ prefix: 'channels', separator: '.', caseSensitive: true }),
      withLocalStorage(),
      withSessionStorage()
    ),
  ]
})
export class AppModule { }
